import React from 'react';
import { Card, CardContent } from './ui/card';
import {
  Target,
  Users,
  Heart,
  KeyRound,
  LayoutDashboard,
  Banknote,
  BadgeCheck,
  FolderKanban,
  Award,
} from 'lucide-react';
import ClientLogosMarquee from './ClientLogosMarquee';

const AboutUsSection = ({
  showTitle = true,
  showInfoCards = true,
  sectionHeading = 'О нас',
  advantagesHeading = 'Наши преимущества',
  hideAdvantagesHeading = false,
  projectsAnchorId,
}) => {
  const renderText = (text) => {
    return text.split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const infoCards = [
    {
      icon: Target,
      title: 'Миссия',
      description:
        'Помогать компаниям покупать, учитывать и продлевать\nлицензии и подписки на ПО без лишних подрядчиков',
    },
    {
      icon: Users,
      title: 'Кто мы',
      description: 'Поставщик лицензий и подписок на ПО\nдля юридических лиц',
    },
    {
      icon: Heart,
      title: 'О главном',
      description: 'Один сервис учёта — сами или через менеджера;\nоплата в рублях и закрывающие документы',
    },
  ];

  const advantages = [
    {
      icon: KeyRound,
      title: 'Купить и продлить',
      description:
        'Лицензии и подписки на ПО, включая AI-сервисы — покупка и продление в одном контуре.',
    },
    {
      icon: LayoutDashboard,
      title: 'Учёт в одном окне',
      description: 'Ведите учёт сами или делегируйте закупку и продление менеджеру в чате.',
    },
    {
      icon: Banknote,
      title: 'Оплата в рублях',
      description: 'Безналичный расчёт для юрлиц, полный пакет закрывающих документов.',
    },
    {
      icon: BadgeCheck,
      title: 'Гарантия лучшей цены',
      description: 'Подбор и сравнение предложений под задачу и бюджет компании.',
    },
    {
      icon: FolderKanban,
      title: 'Каталог по задачам',
      description: 'Задача → вендоры и продукты в каталоге решений /solutions.',
    },
  ];

  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src="/images/about-background.svg"
          alt="Background decoration"
          className="w-full h-full object-cover opacity-30"
        />
      </div>
      <div className="relative z-10">
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
            {sectionHeading}
          </h2>
          {showTitle && (
            <p className="text-lg text-muted-foreground max-w-2xl">
              Антология — поставщик лицензий и подписок на ПО для компаний.
            </p>
          )}
        </div>

        {showInfoCards ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {infoCards.map((card, idx) => (
              <Card
                key={idx}
                className="bg-card/30 border-border/30 hover:border-accent/30 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <card.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-2 text-foreground">{card.title}</h3>
                  <p className="text-sm text-muted-foreground">{renderText(card.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        <div className="mb-12">
          {!hideAdvantagesHeading && (
            <h3 className="text-2xl font-bold text-foreground mb-6">{advantagesHeading}</h3>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage, idx) => (
              <Card
                key={idx}
                className="bg-card/30 border-border/30 hover:border-accent/30 transition-colors"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <advantage.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h4 className="font-semibold mb-2 text-foreground">{advantage.title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {renderText(advantage.description)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div
          id={projectsAnchorId}
          className={`text-center${projectsAnchorId ? ' scroll-mt-24' : ''}`}
        >
          <Card className="bg-card/30 border-border/30">
            <CardContent className="p-8">
              <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <p className="text-2xl font-bold text-foreground mb-2">Опыт с 2017 года</p>
              <p className="text-sm text-muted-foreground mb-6">
                {renderText(
                  'Поставка лицензий и подписок на ПО для компаний. Внедрение ИИ и интеграция — отдельный контур на /services.',
                )}
              </p>

              <div className="mt-8">
                <ClientLogosMarquee />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;
